import React, { CSSProperties } from 'react';
import { layoutTypeState, menuHeightState } from '@atom/setting/homepageSet';
import { useRecoilValue } from 'recoil';
import styled, { css } from 'styled-components';
import { LAYOUT_TYPE, LAYOUT_TYPE_VALUE } from '@typings/setting/layoutType';
import {
  PAGE_LAYOUT_TYPE,
  PAGE_LAYOUT_TYPE_VALUE,
} from '@typings/pages/pageLayout.interface';

export const PageHeaderSec = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 6px;
  margin: 0 auto 16px;

  @media (max-width: 460px) {
    margin: 0px !important;
  }
`;

export const ControlBtnSec = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const tabLayoutTypeMap = {
  [LAYOUT_TYPE.MINIHOME]: css`
    grid-template-areas: 'buttons content';
    grid-template-columns: 200px auto;
  `,
  [LAYOUT_TYPE.THREEDAN]: css`
    grid-template-areas:
      'buttons'
      'content';
    grid-template-columns: auto;
  `,
  [LAYOUT_TYPE.MEMO]: css`
    grid-template-areas:
      'buttons'
      'content';
    grid-template-columns: auto;
  `,
};

export const TapLayoutWrapper = styled.div<{
  tabBtnWidth?: number;
  layoutType?: LAYOUT_TYPE_VALUE;
}>`
  max-width: 1180px;
  text-align: left;
  justify-content: inherit;
  display: grid;
  gap: 24px;

  grid-template-areas: 'buttons content';
  grid-template-columns: 200px auto;
  ${({ layoutType }) => layoutType && tabLayoutTypeMap[layoutType]}

  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
  }
`;

const layoutTypeMap = {
  [LAYOUT_TYPE.MEMO]: css`
    padding: 16px;
    min-height: unset;
    height: unset;
  `,
  [LAYOUT_TYPE.BLOG]: css`
    padding: 0;
    min-height: unset;
    height: unset;
    align-items: flex-start;
  `,

  [LAYOUT_TYPE.THREEDAN]: css`
    padding: 40px 16px;
  `,
  [LAYOUT_TYPE.GAMEUI]: css`
    padding: 100px 16px 40px 16px;
  `,
};

const centerLayoutTypeMap = {
  [LAYOUT_TYPE.TOP]: css`
  `,
  [LAYOUT_TYPE.LEFT]: css`
  `,
  [LAYOUT_TYPE.MEMO]: css`
    padding: 16px;
    min-height: unset;
    align-items: flex-start;
  `,
  [LAYOUT_TYPE.BLOG]: css`
    min-height: unset;
    height: unset;
    align-items: flex-start;
  `,
  [LAYOUT_TYPE.MINIHOME]: css`
  `,

  [LAYOUT_TYPE.THREEDAN]: css`
    padding: 40px 16px;
  `,
};

const pageLayoutTypeMap = {
  [PAGE_LAYOUT_TYPE.ROLLPLAY]: css`
    padding: 40px 0;
    border-left: 1px solid var(--lineColor);
    border-right: 1px solid var(--lineColor);
    min-height: 100dvh;
    height: 100%;
    background-color: var(--cardBackgroundColor);
    backdrop-filter: blur(12px);

    @media (max-width: 768px) {
      padding: calc(60px + env(safe-area-inset-top, 0px)) 0 calc(120px + env(safe-area-inset-bottom, 0px));
    }
  `,
  [PAGE_LAYOUT_TYPE.BLOG]: css`
    padding: 0 ;
  `,
};

export const PageCenterLayout = styled.div<{
  layoutType?: LAYOUT_TYPE_VALUE;
  menuHeight?: string;
}>`
  min-height: ${({ layoutType, menuHeight }) =>
    layoutType === LAYOUT_TYPE.TOP
      ? `calc(100dvh - ${menuHeight || '80px'})`
      : '100dvh'};
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 1080px;
  margin: 0 auto;
  padding: 60px 0;

  ${({ layoutType }) => layoutType && centerLayoutTypeMap[layoutType]};

  @media (max-width: 480px) {
    height: 100%;
    display: block;
    padding: calc(60px + env(safe-area-inset-top, 0px)) 16px calc(120px + env(safe-area-inset-bottom, 0px));
    width: 100% !important;
  }
`;

export const PageLayoutWrapper = styled.div<{
  layoutType: LAYOUT_TYPE_VALUE;
  docLayout?: PAGE_LAYOUT_TYPE_VALUE;
  menuHeight?: string;
}>`
  padding: 60px 0;
  width: 100%;
  margin: 0 auto;
  max-width: 960px;
  min-height: ${({ layoutType, menuHeight }) =>
    layoutType === LAYOUT_TYPE.TOP
      ? `calc(100dvh - ${menuHeight || '80px'})`
      : '100dvh'};

  ${({ layoutType }) => layoutTypeMap[layoutType]};

  @media (max-width: 768px) {
    padding: calc(60px + env(safe-area-inset-top, 0px)) 16px calc(120px + env(safe-area-inset-bottom, 0px));
    border-left: 0px !important;
    border-right: 0px !important;
  }

  @media (max-width: 480px) {
    max-width: 100% !important;
  }

  /* docLayout 전용 스타일은 기본 미디어쿼리 뒤에 위치해야
     ROLLPLAY(스레드 게시판)의 모바일 padding: 72px 0 이 올바르게 적용됨 */
  ${({ docLayout }) => docLayout && pageLayoutTypeMap[docLayout]};
`;

type Props = {
  children: JSX.Element | JSX.Element[] | any;
  style?: CSSProperties;
  docLayout?: PAGE_LAYOUT_TYPE_VALUE;
};
function PageLayout({ children, style, docLayout }: Props) {
  const layoutType = useRecoilValue(layoutTypeState);
  const menuHeight = useRecoilValue(menuHeightState);

  return (
    <PageLayoutWrapper
      layoutType={layoutType}
      menuHeight={menuHeight}
      style={style}
      id="page-layout"
      docLayout={docLayout}
    >
      {children}
    </PageLayoutWrapper>
  );
}

export default PageLayout;
